// Single underwriting engine for every deck: builder, presenter and public viewer all read from here.
export const DECK_THEME = {
  navy: "#183650", charcoal: "#2B3A46", warm: "#F7F9FA", neutral: "#F1F4F6", line: "#D9E1E6",
  muted: "#7A8694", accent: "#2C6B66", accentSoft: "#EEF4F3",
  serif: "Newsreader, Georgia, serif", sans: "'Hanken Grotesk', system-ui, sans-serif", mono: "'IBM Plex Mono', monospace",
};

const n = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const x = Number(String(v).replace(/[$,%\s]/g, ""));
  return Number.isFinite(x) ? x : null;
};
const z = (v) => n(v) ?? 0;

export const ASSUMPTION_GROUPS = [
  { title: "Site & Program", fields: [
    ["site_acreage", "Site acreage", "num"], ["rentable_sf", "Rentable SF", "num"], ["units", "Number of units", "num"],
    ["lots", "Number of lots", "num"], ["parking_count", "Parking count", "num"], ["parking_ratio", "Parking ratio (/1,000 SF)", "num"]] },
  { title: "Revenue & Operations", fields: [
    ["rent_psf", "Rent / SF (annual)", "money"], ["occupancy_pct", "Occupancy", "pct"], ["other_revenue", "Other revenue (annual)", "money"],
    ["opex", "Operating expenses (annual, blank = load %)", "money"], ["expense_load_pct", "Expense / reserve load", "pct"], ["ground_rent", "Ground rent (annual)", "money"], ["cap_rate_pct", "Cap rate", "pct"]] },
  { title: "Development Cost", fields: [
    ["land_cost", "Land / site control", "money"], ["predevelopment_costs", "Predevelopment costs", "money"],
    ["entitlement_costs", "Entitlement costs", "money"], ["hard_costs", "Hard costs", "money"],
    ["soft_costs", "Soft costs", "money"], ["contingency_pct", "Contingency (% of hard + soft)", "pct"]] },
  { title: "Capital & Exit", fields: [
    ["construction_debt", "Construction debt ($, blank = LTC)", "money"], ["ltc_pct", "Loan-to-cost", "pct"],
    ["interest_rate_pct", "Interest rate", "pct"], ["sale_cost_pct", "Sale cost", "pct"],
    ["refi_ltv_pct", "Refinance loan-to-value", "pct"], ["build_months", "Build timeline (months)", "num"]] },
];

export function computeModel(a = {}, exitsCfg = []) {
  const sf = z(a.rentable_sf);
  const occ = (n(a.occupancy_pct) ?? 95) / 100;
  const gpr = sf * z(a.rent_psf);
  const egi = gpr * occ;
  const revenue = egi + z(a.other_revenue);
  const opexBase = n(a.opex) ?? revenue * z(a.expense_load_pct) / 100;
  const opex = opexBase + z(a.ground_rent);
  const noi = revenue - opex;
  const cap = (n(a.cap_rate_pct) ?? 0) / 100;
  const value = cap > 0 && noi > 0 ? noi / cap : 0;
  const contingency = (z(a.hard_costs) + z(a.soft_costs)) * z(a.contingency_pct) / 100;
  const lines = Array.isArray(a.budget_lines) && a.budget_lines.length ? a.budget_lines : null;
  const budget = lines ? lines.map((l) => [l.label || "Line item", z(l.amount)]) : [
    ["Land / site control", z(a.land_cost)], ["Predevelopment", z(a.predevelopment_costs)],
    ["Entitlements", z(a.entitlement_costs)], ["Hard costs", z(a.hard_costs)],
    ["Soft costs", z(a.soft_costs)], ["Contingency", contingency],
  ];
  const baseCost = budget.reduce((s, [, v]) => s + v, 0);
  const debt = n(a.construction_debt) ?? baseCost * z(a.ltc_pct) / 100;
  const months = z(a.build_months);
  const interest = lines ? 0 : debt * z(a.interest_rate_pct) / 100 * (months / 12) * 0.55;
  if (interest > 0) budget.push(["Construction interest (est.)", interest]);
  const totalCost = baseCost + interest;
  const equity = Math.max(0, totalCost - debt);
  const saleCost = value * z(a.sale_cost_pct) / 100;
  const refiProceeds = value * z(a.refi_ltv_pct) / 100;
  const yoc = totalCost > 0 ? noi / totalCost : 0;
  const m = { gpr, egi, revenue, opex, opexBase, groundRent: z(a.ground_rent), noi, lines, cap, value, budget, baseCost, interest, totalCost, debt, equity,
    saleCost, saleCostPct: z(a.sale_cost_pct) / 100, refiProceeds, yoc, cashOut: refiProceeds - debt, devSpread: value - totalCost };
  m.exits = computeExits(m, exitsCfg);
  return m;
}

const AUTO = {
  build_sell: (m) => ({ cost: m.totalCost, value: m.value }),
  build_hold: (m) => ({ cost: m.totalCost, value: m.value }),
  refinance: (m) => ({ cost: m.totalCost, value: m.refiProceeds }),
};
const NO_SALE = new Set(["build_hold", "refinance"]);

export function computeExits(m, cfg = []) {
  return (cfg || []).filter((e) => e.enabled !== false).map((e) => {
    const auto = AUTO[e.key] ? AUTO[e.key](m) : null;
    const cost = n(e.cumulative_cost) ?? (auto ? auto.cost : null);
    const value = n(e.exit_value) ?? (auto ? auto.value : null);
    const ready = cost !== null && value !== null && (cost > 0 || value > 0);
    const gross = ready ? value - cost : null;
    const net = ready ? gross - (NO_SALE.has(e.key) ? 0 : value * m.saleCostPct) : null;
    const equityIn = auto ? m.equity : cost;
    const em = ready && equityIn > 0 ? (equityIn + net) / equityIn : null;
    const months = n(e.months);
    const irr = em !== null && em > 0 && months > 0 ? Math.pow(em, 12 / months) - 1 : null;
    return { ...e, cost, value, ready, remaining: cost !== null ? Math.max(0, m.totalCost - cost) : null,
      gross, net, poc: ready && cost > 0 ? net / cost : null, em, irr, auto: !!auto };
  });
}

export const fmt$ = (v, compact = true) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const neg = v < 0; const a = Math.abs(v);
  const s = compact && a >= 1e6 ? `$${(a / 1e6).toFixed(2)}M` : compact && a >= 1e4 ? `$${Math.round(a / 1e3).toLocaleString()}K` : `$${Math.round(a).toLocaleString()}`;
  return neg ? `(${s})` : s;
};
export const fmtPct = (v, d = 1) => (v === null || v === undefined || !Number.isFinite(v) ? "—" : `${(v * 100).toFixed(d)}%`);
export const fmtX = (v) => (v === null || v === undefined || !Number.isFinite(v) ? "—" : `${v.toFixed(2)}x`);
export const fmtNum = (v) => (n(v) === null ? "—" : n(v).toLocaleString());

export const PUBLIC_DECK_HOST = "arqitek.co";
export const deckUrl = (slug) => `https://${PUBLIC_DECK_HOST}/${slug}`;
export const previewUrl = (slug) => `${window.location.origin}/${slug}`;
export const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "").slice(0, 80);
